import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Typography from '../Typography';
import { theme } from '../../theme';
import { getVehicleIcon, getDefaultIconProps } from '../../utils/vehicleIcons';

const VehicleListItem = ({ 
  vehicle, 
  selected = false, 
  disabled = false, 
  onClick 
}) => {
  const VehicleIconComponent = getVehicleIcon(vehicle.vehicleType);
  const iconProps = {
    ...getDefaultIconProps(disabled ? theme.colors.defaultWeak : theme.fill.brandMain),
    width: 80,
    height: 60
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(vehicle);
    }
  };

  return (
    <VehicleCard
      selected={selected}
      $disabled={disabled}
      onClick={handleClick}
    >
      {vehicle.image ? (
        <VehicleImage src={vehicle.image} alt={`${vehicle.brand} ${vehicle.model}`} />
      ) : (
        <VehicleIconPlaceholder>
          <VehicleIconComponent {...iconProps} />
        </VehicleIconPlaceholder>
      )}
      <VehicleInfo>
        {vehicle.nickname ? (
          <>
            <Typography 
              $variant="body-1-medium" 
              color={disabled ? theme.colors.defaultWeak : theme.colors.defaultMain}
            >
              {vehicle?.brand}
              <Typography 
                as="span" 
                color={disabled ? theme.colors.defaultWeak : theme.colors.defaultStrong} 
                style={{ marginLeft: '4px' }}
              >
                {vehicle?.model}
              </Typography>
            </Typography>
            <Typography 
              $variant="title-5-semibold" 
              color={disabled ? theme.colors.defaultWeak : theme.colors.defaultMain}
            >
              {vehicle.nickname}
            </Typography>
            <Typography 
              $variant="body-3-medium" 
              color={disabled ? theme.colors.defaultWeak : theme.colors.defaultStrong}
            >
              {vehicle.year}
            </Typography>
          </>
        ) : (
          <>
            <Typography 
              $variant="body-1-medium" 
              color={disabled ? theme.colors.defaultWeak : theme.colors.defaultMain}
            >
              {vehicle?.brand}
            </Typography>
            <Typography 
              $variant="title-5-semibold" 
              color={disabled ? theme.colors.defaultWeak : theme.colors.defaultMain}
            >
              {vehicle.model}
            </Typography>
            <Typography 
              $variant="body-3-medium" 
              color={disabled ? theme.colors.defaultWeak : theme.colors.defaultStrong}
            >
              {vehicle.year}
            </Typography>
          </>
        )}
      </VehicleInfo>
    </VehicleCard>
  );
};

VehicleListItem.propTypes = {
  vehicle: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    nickname: PropTypes.string,
    image: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    vehicleType: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

export default VehicleListItem;

// Styled Components
const VehicleCard = styled.ul`
  align-items: flex-start;
  border: 2px solid ${({ selected, theme, $disabled }) =>
    $disabled
      ? theme.border.defaultSubtle
      : (selected ? theme.colors.brandMain : theme.border.defaultSubtle)
  };
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.xs};
  transition: border 0.3s ease;
  width: 100%;
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};

  &:hover {
    border-color: ${({ theme, $disabled }) =>
    $disabled
      ? theme.border.defaultSubtle
      : theme.colors.brandMain
  };
  }
`;

const VehicleInfo = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const VehicleImage = styled.img`
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  height: 80px;
  object-fit: cover;
  width: auto;
`;

const VehicleIconPlaceholder = styled.div`
  align-items: center;
  aspect-ratio: 4 / 3;
  background-color: ${({ theme }) => theme.fill.inverseWeak};
  border-radius: 8px;
  display: flex;
  height: 80px;
  justify-content: center;
  width: auto;
`;